import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { settingsService } from '@/services/settingsService'

export const useMaintenanceStore = defineStore('maintenance', () => {
  const isMaintenanceMode = ref<boolean>(false)
  // Enabled unless explicitly switched off in Strapi settings
  const isAccessibilityWidgetEnabled = ref<boolean>(true)
  const isLoading = ref<boolean>(false)
  const lastChecked = ref<Date | null>(null)

  const shouldCheckMaintenance = computed(() => {
    if (!lastChecked.value) return true

    // Check every 5 minutes
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000)
    return lastChecked.value < fiveMinutesAgo
  })

  const checkMaintenanceMode = async (): Promise<boolean> => {
    if (isLoading.value && !shouldCheckMaintenance.value) {
      return isMaintenanceMode.value
    }

    isLoading.value = true

    try {
      const settings = await settingsService.getAppSettings()

      if (settings) {
        // Skip maintenance mode in development
        isMaintenanceMode.value = import.meta.env.DEV ? false : settings.maintenance_mode
        isAccessibilityWidgetEnabled.value = settings.accessibility_widget !== false
      } else {
        // Default to false if we can't fetch settings
        isMaintenanceMode.value = false
      }

      lastChecked.value = new Date()
      return isMaintenanceMode.value
    } catch (error) {
      console.error('Error checking maintenance mode:', error)
      // On error, default to false to allow app to function
      isMaintenanceMode.value = false
      return false
    } finally {
      isLoading.value = false
    }
  }

  const setMaintenanceMode = (mode: boolean) => {
    isMaintenanceMode.value = mode
    lastChecked.value = new Date()
  }

  return {
    isMaintenanceMode: computed(() => isMaintenanceMode.value),
    isLoading: computed(() => isLoading.value),
    isAccessibilityWidgetEnabled: computed(() => isAccessibilityWidgetEnabled.value),
    checkMaintenanceMode,
    setMaintenanceMode,
    shouldCheckMaintenance,
  }
})
